import { z } from "zod";

// Widget configuration validation schemas
export const WidgetConfigurationSchema = z.object({
  chartType: z.string().optional(),
  filters: z.record(z.any()).optional(),
  refreshInterval: z.number().min(30).max(3600).optional(),
  size: z.enum(['small', 'medium', 'large']).optional(),
  customQuery: z.string().optional(),
  limit: z.number().min(1).max(1000).optional(),
  groupBy: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export const WidgetPositionSchema = z.object({
  x: z.number().min(0),
  y: z.number().min(0),
  w: z.number().min(1).max(12),
  h: z.number().min(1).max(12),
});

export const CreateWidgetSchema = z.object({
  dashboard_id: z.string().uuid(),
  title: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  widget_type: z.enum(['metric', 'chart', 'list', 'progress', 'calendar', 'activity']),
  data_source: z.enum(['organizations', 'contacts', 'opportunities', 'activities', 'client_growth', 'gifts', 'custom_query']),
  configuration: WidgetConfigurationSchema.optional(),
  position: WidgetPositionSchema.optional(),
});

// Data validation schemas for different widget types
export const MetricDataSchema = z.object({
  value: z.number(),
  label: z.string().optional(),
  trend: z.object({
    value: z.number(),
    direction: z.enum(['up', 'down', 'neutral']),
  }).optional(),
  subtitle: z.string().optional(),
});

export const ChartDataSchema = z.array(z.object({
  name: z.string(),
  value: z.number(),
  label: z.string().optional(),
  color: z.string().optional(),
}));

export const ListDataSchema = z.array(z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  value: z.union([z.string(), z.number()]).optional(),
  metadata: z.record(z.any()).optional(),
}));

// Widget error types
export enum WidgetErrorType {
  DATA_FETCH_ERROR = 'DATA_FETCH_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',
  RENDERING_ERROR = 'RENDERING_ERROR',
  PERMISSION_ERROR = 'PERMISSION_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR',
}

export class WidgetError extends Error {
  public readonly type: WidgetErrorType;
  public readonly widgetId?: string;
  public readonly recoverable: boolean;
  public readonly retryAfter?: number;

  constructor(
    type: WidgetErrorType,
    message: string,
    options: {
      widgetId?: string;
      recoverable?: boolean;
      retryAfter?: number;
      cause?: Error;
    } = {}
  ) {
    super(message);
    this.name = 'WidgetError';
    this.type = type;
    this.widgetId = options.widgetId;
    this.recoverable = options.recoverable ?? true;
    this.retryAfter = options.retryAfter;
    
    if (options.cause) {
      this.cause = options.cause;
    }
  }
}

// Validation functions
export function validateWidgetConfiguration(config: unknown): z.infer<typeof CreateWidgetSchema> {
  try {
    return CreateWidgetSchema.parse(config);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new WidgetError(
        WidgetErrorType.VALIDATION_ERROR,
        `Invalid widget configuration: ${error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ')}`,
        { recoverable: false }
      );
    }
    throw error;
  }
}

export function validateWidgetData(data: unknown, expectedSchema: z.ZodSchema): any {
  try {
    return expectedSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new WidgetError(
        WidgetErrorType.VALIDATION_ERROR,
        `Invalid widget data: ${error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ')}`,
        { recoverable: true }
      );
    }
    throw error;
  }
}

// Data sanitization functions
export function sanitizeCustomQuery(query: string): string {
  // Remove dangerous SQL operations
  const dangerousPatterns = [
    /\b(DROP|DELETE|INSERT|UPDATE|ALTER|CREATE|EXEC|EXECUTE)\b/gi,
    /\b(UNION|--|\*\/|\/\*)\b/gi,
    /[;](?=(?:[^']*'[^']*')*[^']*$)/g, // Semicolons outside of strings
  ];

  const sanitized = query.trim();
  
  for (const pattern of dangerousPatterns) {
    if (pattern.test(sanitized)) {
      throw new WidgetError(
        WidgetErrorType.VALIDATION_ERROR,
        'Custom query contains potentially dangerous operations',
        { recoverable: false }
      );
    }
  }

  // Ensure query starts with SELECT
  if (!sanitized.toLowerCase().startsWith('select')) {
    throw new WidgetError(
      WidgetErrorType.VALIDATION_ERROR,
      'Custom queries must start with SELECT',
      { recoverable: false }
    );
  }

  return sanitized;
}

export function sanitizeWidgetTitle(title: string): string {
  return title
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .substring(0, 100); // Limit length
}

export function sanitizeWidgetDescription(description: string): string {
  return description
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .substring(0, 500); // Limit length
}

// Error recovery strategies
export function getErrorRecoveryStrategy(error: WidgetError): {
  canRetry: boolean;
  retryDelay: number;
  fallbackData?: any;
  userMessage: string;
} {
  switch (error.type) {
    case WidgetErrorType.DATA_FETCH_ERROR:
      return {
        canRetry: true,
        retryDelay: 5000,
        userMessage: 'Failed to load data. Retrying...',
      };

    case WidgetErrorType.RATE_LIMIT_ERROR:
      return {
        canRetry: true,
        retryDelay: error.retryAfter || 60000,
        userMessage: 'Rate limit exceeded. Please wait before refreshing.',
      };

    case WidgetErrorType.PERMISSION_ERROR:
      return {
        canRetry: false,
        retryDelay: 0,
        userMessage: 'You do not have permission to view this data.',
      };

    case WidgetErrorType.CONFIGURATION_ERROR:
      return {
        canRetry: false,
        retryDelay: 0,
        userMessage: 'Widget configuration is invalid. Please reconfigure.',
      };

    case WidgetErrorType.VALIDATION_ERROR:
      return {
        canRetry: false,
        retryDelay: 0,
        userMessage: 'Data validation failed. Please check your configuration.',
      };

    case WidgetErrorType.RENDERING_ERROR:
      return {
        canRetry: true,
        retryDelay: 1000,
        fallbackData: { error: true, message: 'Rendering failed' },
        userMessage: 'Display error occurred. Retrying...',
      };

    default:
      return {
        canRetry: true,
        retryDelay: 5000,
        userMessage: 'An unexpected error occurred. Retrying...',
      };
  }
}

// Widget data transformers with error handling
export function transformOpportunityData(rawData: any[]): any {
  try {
    if (!Array.isArray(rawData)) {
      throw new WidgetError(
        WidgetErrorType.VALIDATION_ERROR,
        'Expected array of opportunities',
        { recoverable: false }
      );
    }

    return rawData.map(item => ({
      id: item.id || 'unknown',
      name: item.name || 'Unnamed Opportunity',
      value: typeof item.value === 'number' ? item.value : 0,
      stage: item.stage || 'Unknown',
      organization: item.organization?.name || 'Unknown Organization',
      probability: typeof item.probability === 'number' ? item.probability : 0,
    }));
  } catch (error) {
    if (error instanceof WidgetError) {
      throw error;
    }
    throw new WidgetError(
      WidgetErrorType.DATA_FETCH_ERROR,
      `Failed to transform opportunity data: ${error.message}`,
      { recoverable: true, cause: error }
    );
  }
}

export function transformOrganizationData(rawData: any[]): any {
  try {
    if (!Array.isArray(rawData)) {
      throw new WidgetError(
        WidgetErrorType.VALIDATION_ERROR,
        'Expected array of organizations',
        { recoverable: false }
      );
    }

    return rawData.map(item => ({
      id: item.id || 'unknown',
      name: item.name || 'Unnamed Organization',
      tier: item.tier || 'Unknown',
      status: item.status || 'Unknown',
      relationship_health: typeof item.relationship_health === 'number' ? item.relationship_health : 0,
    }));
  } catch (error) {
    if (error instanceof WidgetError) {
      throw error;
    }
    throw new WidgetError(
      WidgetErrorType.DATA_FETCH_ERROR,
      `Failed to transform organization data: ${error.message}`,
      { recoverable: true, cause: error }
    );
  }
}